/**
 * Elektron jurnal moduli — umumiy tiplar va kichik yordamchilar.
 *
 * Manba: `docs/ELEKTRON-JURNAL.md`. Tiplar hujjatning **§6 "Backend amalga oshirildi —
 * javob shakllari"** bo'limiga qarab yozilgan: u §1–§2.5 ni ALMASHTIRMAYDI, aniqlashtiradi
 * va ishlayotgan kod bilan bir xil, shu sabab kelishmovchilikda §6 ustun.
 */

/** Chorak (`/academic-terms`). §6.0: barcha javoblarda `term` AYNAN shu shaklda. */
export interface AcademicTerm {
  id: string;
  academicYearId: string;
  name: string;
  /** 1..4 — tartib va saralash uchun */
  index: number;
  /** Toshkent kun BOSHI */
  startDate: string;
  /** Toshkent kun OXIRI (inclusive) */
  endDate: string;
}

/** Jurnal katagidagi bitta joriy baho. */
export interface JournalCell {
  id: string;
  value: number;
  comment?: string | null;
  teacherId?: string | null;
}

/**
 * Muzlatilgan chorak bahosi. `suggested` — YOZILGAN paytdagi taklif (audit uchun),
 * o'quvchi qatoridagi `suggested` esa HOZIRGI taklif. Ular farq qilishi normal:
 * chorak bahosi qo'yilgandan keyin yangi joriy baho qo'shilgan bo'lishi mumkin.
 */
export interface TermGradeRecord {
  id?: string;
  value: number;
  suggested?: number | null;
  gradeCount?: number | null;
  comment?: string | null;
  teacherId?: string | null;
}

/** Jurnal qatori — bitta o'quvchi (§6.2). */
export interface JournalStudent {
  id: string;
  fullName: string;
  /** Sana ("YYYY-MM-DD", Toshkent kalendar kuni) → o'sha kundagi baholar. */
  grades: Record<string, JournalCell[] | undefined>;
  /** JORIY baholar o'rtachasi (baho yo'q bo'lsa `null`). */
  avg: number | null;
  gradeCount?: number;
  /** HOZIRGI taklif (o'rtachadan yaxlitlangan); baho yo'q bo'lsa `null`. */
  suggested?: number | null;
  termGrade?: TermGradeRecord | null;
  /* TANLANGAN DAVR (hafta) ko'rsatkichlari — jurnal tabi shularni chizadi.
     Chorak qiymatlari (`avg`/`suggested`) chorak yakuni ekrani uchun qoladi:
     ikkalasi bitta javobda kelgani uchun ular hech qachon farq qilmaydi. */
  periodAvg?: number | null;
  periodCount?: number;
  periodSuggested?: number | null;
}

/**
 * `GET /grades/journal?classId=&subjectId=&termId=` (§6.2).
 * `termId` IXTIYORIY; chorak topilmasa `term: null` va bo'sh matritsa qaytadi (400 emas).
 */
export interface JournalResponse {
  term: AcademicTerm | null;
  dates: string[];
  students: JournalStudent[];
}

/** `GET /term-grades` qatori (§6.5). */
export interface TermGradeRow {
  studentId: string;
  fullName: string;
  avg: number | null;
  /** HOZIRGI taklif — joriy baholardan. */
  suggested: number | null;
  gradeCount: number;
  /** Qo'yilgan chorak bahosi yoki `null`. */
  termGrade?: TermGradeRecord | null;
}

/** `GET /term-grades?classId=&subjectId=&termId=` — uchala parametr MAJBURIY (§6.5). */
export interface TermGradesResponse {
  term: AcademicTerm | null;
  classId?: string;
  subjectId?: string;
  rows: TermGradeRow[];
}

/** `GET /reports/grades` qatori (§6.6). `termGrade` — OBYEKT yoki `null`, raqam EMAS. */
export interface GradeReportRow {
  studentId: string;
  fullName: string;
  gradeCount: number;
  avg: number | null;
  suggested?: number | null;
  termGrade?: TermGradeRecord | null;
}

export interface GradeReportSummary {
  /** CHORAK baholarining o'rtachasi (joriy baholarniki emas); hech kimga qo'yilmagan bo'lsa `null`. */
  avg: number | null;
  excellent: number;
  good: number;
  satisfactory: number;
  poor: number;
  /** Chorak bahosi qo'yilganlar soni. */
  graded: number;
  /** Sinfdagi o'quvchilar soni. */
  total: number;
}

export interface GradeReportResponse {
  term: AcademicTerm | null;
  rows: GradeReportRow[];
  summary: GradeReportSummary;
}

/**
 * Lokal sana → "YYYY-MM-DD". `toISOString()` ATAYLAB ishlatilmaydi — UTC+5 da
 * lokal yarim tun bir kun orqaga siljib, noto'g'ri kun yuborilardi
 * (`pages/Attendance.tsx` dagi bir xil yordamchi).
 */
export function isoDate(d: Date | null | undefined): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "YYYY-MM-DD" → Date (lokal kun boshi). Xom `new Date('YYYY-MM-DD')` UTC deb o'qirdi. */
export function fromIsoDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Sana ustunini qisqa ko'rsatish: "2026-09-02" → "02.09". */
export function shortDate(s: string): string {
  const d = fromIsoDate(s);
  return d ? `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}` : s;
}

/** Baho rangi (Mantine) — jadval, badge va jurnal kataklari uchun YAGONA joy. */
export function gradeColor(v: number | null | undefined): string {
  if (v == null) return 'gray';
  if (v >= 4) return 'teal';
  if (v === 3) return 'yellow';
  return 'red';
}

/** O'rtacha bahoni bir xonali kasr bilan; qiymat yo'q bo'lsa tire. */
export function fmtAvg(v: number | null | undefined): string {
  return v == null || Number.isNaN(v) ? '—' : v.toFixed(1);
}

/** Sana qaysi chorakka tushishini topadi (baho qo'yishda chorakni avtomatik tanlash). */
export function termForDate(terms: AcademicTerm[] | undefined, date: Date | null): AcademicTerm | undefined {
  const iso = isoDate(date);
  if (!iso || !terms?.length) return undefined;
  return terms.find((tm) => {
    const s = String(tm.startDate).slice(0, 10);
    const e = String(tm.endDate).slice(0, 10);
    return iso >= s && iso <= e;
  });
}

/** Chorak bahosining qiymati — obyekt (§6.6) ham, eski raqam shakli ham qabul qilinadi. */
export function termGradeValue(v: TermGradeRecord | number | null | undefined): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  return typeof v.value === 'number' ? v.value : null;
}

/**
 * `GET /term-grades` javobidan qatorlarni olish. §6.5 da shakl `{ term, rows }` deb
 * QULFLANGAN; bardoshli o'qish ATAYLAB qoldirildi — kutilmagan shaklda sahifa
 * "ma'lumot yo'q" holatini ko'rsatadi, oq ekran bermaydi.
 */
export function normalizeTermGradeRows(data: unknown): TermGradeRow[] {
  if (Array.isArray(data)) return data as TermGradeRow[];
  if (data && typeof data === 'object') {
    const obj = data as { rows?: unknown; items?: unknown; students?: unknown };
    for (const v of [obj.rows, obj.items, obj.students]) if (Array.isArray(v)) return v as TermGradeRow[];
  }
  return [];
}

/** Axios xatosidan server xabarini olish (backend `{ error }` qaytaradi). */
export function apiError(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;
}

/** Axios xatosining HTTP holati (403 kabi maxsus holatlarni ajratish uchun). */
export function apiStatus(e: unknown): number | undefined {
  return (e as { response?: { status?: number } })?.response?.status;
}
